import { useEffect, useState } from "react";
import { ChevronDown, NotebookPen, FileText, BookOpenText, Maximize2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const SetupPanel = ({ setup, setSetup, onOpenBrief, hasBrief, hasTranscript }) => {
  const [open, setOpen] = useState(!setup.notes && !setup.topic);
  useEffect(() => { if (hasTranscript) setOpen(false); }, [hasTranscript]);
  const set = (k) => (e) => setSetup({ ...setup, [k]: e.target.value });
  return (
    <section className="iv-panel" data-testid="setup-panel">
      <div className="flex items-center gap-2 pr-3">
        <button onClick={() => setOpen(!open)} className="flex flex-1 min-w-0 items-center justify-between px-4 py-3" data-testid="setup-toggle-button">
          <span className="eyebrow flex items-center gap-2"><NotebookPen size={14} /> Prep notes</span>
          <span className="flex items-center gap-2 text-sm text-slate-300 truncate max-w-[55%]">
            {!open && (setup.guest_name || setup.topic) && <span className="truncate">{[setup.guest_name, setup.topic].filter(Boolean).join(" · ")}</span>}
            <ChevronDown size={16} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
          </span>
        </button>
        <button onClick={onOpenBrief} data-testid="open-brief-button"
          className={`shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors ${hasBrief ? "bg-sky-400/15 text-sky-200 hover:bg-sky-400/25" : "bg-white/10 text-white hover:bg-white/20"}`}>
          <FileText size={14} /> Brief
        </button>
      </div>
      {open && (
        <div className="space-y-3 px-4 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input data-testid="setup-guest-name-input" placeholder="Guest name" value={setup.guest_name} onChange={set("guest_name")} className="iv-field" />
            <Input data-testid="setup-topic-input" placeholder="Interview topic" value={setup.topic} onChange={set("topic")} className="iv-field" />
          </div>
          <Textarea data-testid="setup-notes-textarea" placeholder="Paste background research: bio, past interviews, articles, angles you want to cover..."
            value={setup.notes} onChange={set("notes")} className="iv-field min-h-[160px] text-sm leading-relaxed" />
          <p className="text-[0.78rem] font-mono text-slate-400" data-testid="setup-notes-count">{setup.notes.length.toLocaleString()} characters · kept in this browser until you save</p>
        </div>
      )}
    </section>
  );
};

export const BriefGlance = ({ brief, onOpen }) => {
  const [open, setOpen] = useState(true);
  if (!brief) return null;
  return (
    <section className="iv-panel" data-testid="brief-glance">
      <div className="flex items-center justify-between gap-2 pl-4 pr-2 py-2.5 border-b border-white/10">
        <button onClick={() => setOpen(!open)} data-testid="brief-glance-toggle" className="flex flex-1 items-center justify-between">
          <span className="eyebrow flex items-center gap-2"><BookOpenText size={14} /> Key facts</span>
          <ChevronDown size={16} className={`text-slate-300 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
        <button onClick={onOpen} data-testid="brief-glance-open" title="Open full brief" className="iv-icon-btn"><Maximize2 size={14} /></button>
      </div>
      {open && (
        <ul className="px-4 py-3 space-y-1.5 max-h-56 overflow-y-auto" data-testid="brief-glance-facts">
          {brief.key_facts.map((f) => <li key={f} className="flex gap-2 text-base leading-snug text-white"><span className="text-sky-400">•</span><span>{f}</span></li>)}
          {brief.watch_outs?.length > 0 && (
            <li className="pt-2 text-sm text-amber-200" data-testid="brief-glance-watchouts">
              <span className="font-mono text-xs uppercase tracking-widest text-amber-300">Watch out · </span>{brief.watch_outs.join(" · ")}
            </li>
          )}
        </ul>
      )}
    </section>
  );
};
